import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Calendar, Plus, Pencil, Trash2 } from "lucide-react"
import { Button } from "../../components/ui/Button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card"
import { Input } from "../../components/ui/Input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table"
import { TableSkeleton } from "../../components/ui/Skeleton"
import { EmptyState } from "../../components/ui/EmptyState"
import { fr } from "../../locales/fr"
import { useAuth } from "../../context/AuthContext"
import { canManageTeamAndCourses } from "../../utils/roles"
import { getCourses, createCourse, updateCourse, deleteCourse } from "../../services/courseService"

const emptyForm = { name: "", description: "", color: "#6366f1" }

export default function CoursesList() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const canEdit = canManageTeamAndCourses(user)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const data = await getCourses()
      setRows(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error(e)
      setRows([])
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  const startCreate = () => {
    setEditingId(null)
    setForm(emptyForm)
    setShowForm(true)
  }

  const startEdit = (r) => {
    setEditingId(r.id)
    setForm({
      name: r.name || "",
      description: r.description || "",
      color: r.color || "#6366f1",
    })
    setShowForm(true)
  }

  const submit = async (e) => {
    e.preventDefault()
    setSaving(true)
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      color: form.color || null,
    }
    try {
      if (editingId) {
        await updateCourse(editingId, payload)
      } else {
        await createCourse(payload)
      }
      setShowForm(false)
      await load()
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || "Erreur")
    }
    setSaving(false)
  }

  const remove = async (id) => {
    if (!window.confirm(fr.team.confirmDeleteCourse)) return
    try {
      await deleteCourse(id)
      await load()
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || "Erreur")
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-bold text-foreground">{fr.team.coursesTitle}</h1>
          <p className="text-muted-foreground">{fr.team.coursesSubtitle}</p>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{fr.team.coursesCalendarHint}</p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={() => navigate("/calendar")}>
            <Calendar className="mr-2 h-4 w-4" />
            {fr.calendarPage.title}
          </Button>
          {canEdit && (
            <Button type="button" onClick={startCreate}>
              <Plus className="mr-2 h-4 w-4" />
              {fr.team.add}
            </Button>
          )}
        </div>
      </div>

      {canEdit && showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? fr.team.edit : fr.team.add}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <label className="text-sm font-medium">{fr.team.name}</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <label className="text-sm font-medium">{fr.team.description}</label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.team.color}</label>
                <Input
                  type="color"
                  className="h-10 w-24 cursor-pointer p-1"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                />
              </div>
              <div className="flex items-end gap-2">
                <Button type="submit" disabled={saving}>
                  {fr.common.save}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  {fr.common.cancel}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">
              <TableSkeleton rows={5} columns={4} />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6">
              <EmptyState title={fr.team.noCourses} />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{fr.team.name}</TableHead>
                  <TableHead>{fr.team.description}</TableHead>
                  <TableHead className="w-[100px]">{fr.team.color}</TableHead>
                  {canEdit && <TableHead className="w-[100px]">{fr.common.actions}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-muted-foreground">{r.description || fr.team.none}</TableCell>
                    <TableCell>
                      <span
                        className="inline-block h-6 w-6 rounded border border-border"
                        style={{ backgroundColor: r.color || "#ccc" }}
                        title={r.color || ""}
                      />
                    </TableCell>
                    {canEdit && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(r)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => remove(r.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
